/**
 * Per-surface MSAL client config registry.
 *
 * Each Office host (Excel today; Word/Outlook/PowerPoint later) gets its
 * own Microsoft Entra app registration so:
 *   - permissions can be scoped per host (Excel doesn't need Outlook scopes)
 *   - one host's misconfiguration doesn't blast-radius the others
 *   - tenant admins can grant consent per host independently
 *
 * The tenant is shared across surfaces — see
 * `NEXT_PUBLIC_MICROSOFT_TENANT_ID`. Adding a new surface is intentionally
 * a typecheck failure: extending the `OfficeAddinSurface` union without
 * adding the matching registry entry will not compile.
 *
 * See ADR-044 §6 (auth glue) for the auth flow this feeds into.
 */

import type { OfficeAddinSurface } from "@workspace/services/domain";

export interface MsalClientConfig {
  /** Microsoft Entra app registration client ID. */
  clientId: string;
  /** Scopes requested when acquiring tokens via NAA for this surface. */
  scopes: readonly string[];
}

/**
 * Surface → MSAL client config. Add a new entry (and the matching env
 * var) when bringing each host online; the `Record` shape will fail
 * typecheck until every `OfficeAddinSurface` member is present.
 */
export const OFFICE_ADDIN_MSAL_CLIENTS: Record<
  OfficeAddinSurface,
  MsalClientConfig
> = {
  "excel-add-in": {
    clientId: process.env.NEXT_PUBLIC_MS_CLIENT_ID_EXCEL_ADD_IN ?? "",
    scopes: ["User.Read", "Files.Read"],
  },
  "outlook-add-in": {
    clientId: process.env.NEXT_PUBLIC_MS_CLIENT_ID_OUTLOOK_ADD_IN ?? "",
    scopes: ["User.Read", "Mail.Read"],
  },
  "word-add-in": {
    clientId: process.env.NEXT_PUBLIC_MS_CLIENT_ID_WORD_ADD_IN ?? "",
    scopes: ["User.Read", "Files.Read"],
  },
  // Future surfaces — add an entry + matching env var when bringing each
  // one online. Each requires its own Entra app registration with the
  // `brk-multihub://` redirect URI configured for NAA.
  //
  //   "outlook-add-in": {
  //     clientId: process.env.NEXT_PUBLIC_MS_CLIENT_ID_OUTLOOK_ADD_IN ?? "",
  //   },
  //   "powerpoint-add-in": {
  //     scopes: ["User.Read", "Files.Read"],
  //   },
};

/**
 * Look up the MSAL client config for an Office surface. Throws a
 * descriptive error (with the expected env var name) when the surface
 * has no client ID configured — typically a deployment that forgot to
 * set `NEXT_PUBLIC_MS_CLIENT_ID_<SURFACE>`.
 */
export function getMsalClientConfig(
  surface: OfficeAddinSurface
): MsalClientConfig {
  const cfg = OFFICE_ADDIN_MSAL_CLIENTS[surface];
  if (!cfg?.clientId) {
    const envVar = `NEXT_PUBLIC_MS_CLIENT_ID_${surface
      .toUpperCase()
      .replace(/-/g, "_")}`;
    throw new Error(
      `No Entra client ID configured for Office surface "${surface}". ` +
        `Set ${envVar} in your environment.`
    );
  }
  return cfg;
}
